/**
 * ProductionTrackerPage
 *
 * Read-only monitoring page for all in-flight production batches and orders.
 * Each view (Orders / Batches) has its own tab structure:
 *   - Orders: "In Progress" / "Shipped" / "Committed"
 *   - Batches: "In Progress" / "Complete" / "Archived"
 * Features: 5-minute auto-refresh, search, sort, expandable rows.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Activity, MapPin, ArrowUpDown, Layers, ClipboardList, Search, AlertTriangle, RefreshCw, Archive, Timer, CheckCircle2, Package, Inbox, Hourglass, ListChecks, Boxes, Download, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Toggle } from '@/components/ui/toggle';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRole } from '@/hooks/use-role';
import {
  useTrackerCounts,
  useTrackerBatches,
  useTrackerOrders,
  useTrackerQueueOrders,
  useTrackerQueueCounts,
  useArchivedBatches,
  useCommittedOrders,
  useCommittedSummary,
  useTrackerState,
  type BatchTab,
  type OrderTab,
  type QueueViewTab,
  type TrackerView,
} from '../hooks/useProductionTracker';
import { TrackerBatchTable } from './TrackerBatchTable';
import { TrackerOrderTable } from './TrackerOrderTable';
import { TrackerQueueTable } from './TrackerQueueTable';
import { canRelocateQueueOrder } from '../lib/queue-location-actions';
import { OrderDetailModal } from '@/features/production-issues/components/OrderDetailModal';
import { CommittedOrderTable } from './CommittedOrderTable';
import { StatusFilterDropdown } from './StatusFilterDropdown';
import {
  STATUS_LABEL_KEYS,
  FULFILLMENT_STATUS_LABEL_KEYS,
} from '../lib/order-status-display';
import type { CommittedProductionStatus } from '../lib/order-status-display';
import { toCsvContent } from '@/lib/csv';
import { downloadCsvFile } from '@/lib/download';
import { CSV_DATE_TIME_FORMAT, FILE_DATE_FORMAT } from '@/lib/format-dates';
import { PaginationControls } from '@/features/production-orders';
import {
  useClientOrgFilter,
  ALL_CLIENTS,
} from '@/features/inventory/hooks/useClientOrgFilter';
import { ClientFilterDropdown } from '@/features/inventory/components/ClientFilterDropdown';
import type { QueuedOrder } from '@/features/production-orders/types';
import { useLocationInvoiceEnabled } from '@/features/production-orders/hooks/useLocationInvoiceEnabled';
import { useInvoiceBatchData, useInvoiceOrderData } from '@/features/production-orders/hooks/useInvoiceData';
import { usePrintInvoices } from '@/features/production-orders/hooks/usePrintInvoices';
import { OrderInvoice } from '@/features/production-orders/components/invoices/OrderInvoice';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

// ============================================================================
// TAB BADGE
// ============================================================================

function TabBadge({ count, isLoading }: { count: number | undefined; isLoading: boolean }) {
  if (isLoading) {
    return <Skeleton className="h-5 w-6 rounded-full" />;
  }
  if (count === undefined) return null;
  if (count === 0) {
    return (
      <span className="text-muted-foreground text-xs tabular-nums">
        (0)
      </span>
    );
  }
  return (
    <span
      className={cn(
        'text-xs px-1.5 py-0.5 rounded-full tabular-nums min-w-[1.25rem] text-center',
        'bg-primary/15 text-primary font-medium'
      )}
    >
      {count}
    </span>
  );
}

// ============================================================================
// REFRESH BUTTON
// ============================================================================

function RefreshButton({ isFetching, onRefresh }: { isFetching: boolean; onRefresh: () => void }) {
  const { t } = useTranslation('production');
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onRefresh}
      disabled={isFetching}
      className="gap-2"
    >
      <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
      {t('tracker.refresh')}
    </Button>
  );
}

// ============================================================================
// VIEW TOGGLE
// ============================================================================

function ViewToggle({ view, onChange, hideBatches }: { view: TrackerView; onChange: (v: TrackerView) => void; hideBatches?: boolean }) {
  const { t } = useTranslation('production');
  return (
    <div className="flex rounded-md border">
      <Button
        variant={view === 'orders' ? 'default' : 'ghost'}
        size="sm"
        className={cn('gap-1.5 rounded-r-none border-0', view === 'orders' && 'shadow-none')}
        onClick={() => onChange('orders')}
      >
        <ClipboardList className="h-4 w-4" />
        {t('tracker.viewOrders')}
      </Button>
      {/* Batches is a floor-management view — hidden for CX, whose backend
          access covers only the Orders + Queue reads. `view` is plain
          useState defaulting to 'orders', so without this button CX can
          never reach the batches view. */}
      {!hideBatches && (
        <Button
          variant={view === 'batches' ? 'default' : 'ghost'}
          size="sm"
          className={cn('gap-1.5 rounded-none border-0 border-x', view === 'batches' && 'shadow-none')}
          onClick={() => onChange('batches')}
        >
          <Layers className="h-4 w-4" />
          {t('tracker.viewBatches')}
        </Button>
      )}
      <Button
        variant={view === 'queue' ? 'default' : 'ghost'}
        size="sm"
        className={cn('gap-1.5 rounded-l-none border-0', view === 'queue' && 'shadow-none')}
        onClick={() => onChange('queue')}
      >
        <Hourglass className="h-4 w-4" />
        {t('tracker.viewQueue')}
      </Button>
    </div>
  );
}

// ============================================================================
// EMPTY STATES
// ============================================================================

// Empty state i18n keys — resolved at render time via t()
const EMPTY_BATCH_KEYS: Record<Exclude<BatchTab, 'archived'>, { titleKey: string; descKey: string }> = {
  'in-progress': {
    titleKey: 'tracker.emptyBatch.inProgress.title',
    descKey: 'tracker.emptyBatch.inProgress.desc',
  },
  complete: {
    titleKey: 'tracker.emptyBatch.complete.title',
    descKey: 'tracker.emptyBatch.complete.desc',
  },
};

const EMPTY_BATCH_ARCHIVED_KEYS = {
  titleKey: 'tracker.emptyBatch.archived.title',
  descKey: 'tracker.emptyBatch.archived.desc',
};

const EMPTY_ORDER_KEYS: Record<OrderTab, { titleKey: string; descKey: string }> = {
  active: {
    titleKey: 'tracker.emptyOrder.active.title',
    descKey: 'tracker.emptyOrder.active.desc',
  },
  shipped: {
    titleKey: 'tracker.emptyOrder.shipped.title',
    descKey: 'tracker.emptyOrder.shipped.desc',
  },
  committed: {
    titleKey: 'tracker.emptyOrder.committed.title',
    descKey: 'tracker.emptyOrder.committed.desc',
  },
};

// ============================================================================
// COMMITTED EXPORT LIMITS
// ============================================================================

/**
 * The export emits one row per (order, SKU), so the order count is only a floor
 * on the file size — a 9k-order export is comfortably past a spreadsheet's
 * usable size once fanned out. Block above this and tell the operator which
 * controls narrow the set, rather than starting a fetch that will be unusable.
 */
const COMMITTED_EXPORT_ORDER_WARN_LIMIT = 8000;

const EMPTY_QUEUE_KEYS: Record<QueueViewTab, { titleKey: string; descKey: string }> = {
  ready: {
    titleKey: 'tracker.emptyQueue.ready.title',
    descKey: 'tracker.emptyQueue.ready.desc',
  },
  all: {
    titleKey: 'tracker.emptyQueue.all.title',
    descKey: 'tracker.emptyQueue.all.desc',
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ProductionTrackerPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id;
  const { organization } = useOrganization();
  const shopifyDomain = organization?.shopify_domain ?? null;
  const { can, isCX } = useRole();
  const canHoldOrders = can('orders.hold_request');
  // The same permission `route` and the split resolver check server-side, so
  // the menu and the server agree on who may move or split an order.
  const canResolveLocations = can('orders.resolve_locations');
  const navigate = useNavigate();

  const {
    batchTab,
    setBatchTab,
    orderTab,
    setOrderTab,
    queueTab,
    setQueueTab,
    sortOrder,
    setSortOrder,
    currentPage,
    setCurrentPage,
    view,
    setView,
    inBinOnly,
    setInBinOnly,
  } = useTrackerState();

  const [searchInput, setSearchInput] = useState('');
  const typedSearch = useDebouncedValue(searchInput, 300);
  // A search set by code (handleClientQcRedoDone) applies at once, so the tab
  // switch and the search land in ONE list fetch instead of two. Dropped as
  // soon as the debounced typed value moves (it then carries the same text).
  const [appliedSearch, setAppliedSearch] = useState<string | null>(null);
  useEffect(() => { setAppliedSearch(null); }, [typedSearch]);
  const debouncedSearch = appliedSearch ?? typedSearch;

  // BLU-811: Committed-tab production-status filter. `undefined` = all statuses.
  // Reset on any change that can make the selected status vanish (client / location)
  // or that leaves the tab (order tab / view) — see the effect below.
  const [productionStatus, setProductionStatus] = useState<CommittedProductionStatus | undefined>(undefined);
  const isStatusFiltered = productionStatus !== undefined;

  const trpcUtilsQueue = trpc.useUtils();

  // BLU-1462: a client QC redo reopened a handed-over order. Its old batch is
  // finished, so the default Active view hides it: switch to Active and
  // search it (a search drops the batch filter). The dialog's mutation only
  // marks the order lists stale on success, so the new query key fetches
  // once; when the key would not change, refetch it here instead.
  const handleClientQcRedoDone = useCallback(
    (orderName: string) => {
      if (orderTab === 'active' && currentPage === 0 && debouncedSearch === orderName) {
        void trpcUtilsQueue.productionOrders.listTrackerOrders.invalidate();
        return;
      }
      setOrderTab('active');
      setCurrentPage(0);
      setSearchInput(orderName);
      setAppliedSearch(orderName);
    },
    [orderTab, currentPage, debouncedSearch, trpcUtilsQueue, setOrderTab, setCurrentPage]
  );

  // Order Queue hold/skip-hold (BLU-367 CX access) — same mutation wiring as
  // OrdersPage.tsx's kebab actions, so both surfaces stay behaviourally identical.
  const holdOrderMutation = trpc.productionOrders.issues.holdOrder.useMutation({
    onSuccess: () => {
      void trpcUtilsQueue.productionOrders.getOrderQueue.invalidate();
      void trpcUtilsQueue.productionOrders.getQueueCounts.invalidate();
      void trpcUtilsQueue.productionOrders.issues.getOnHoldOrders.invalidate();
      // Orders tab + its tab badges: a held order drops out of the active listing
      // (the RPC excludes ON_HOLD), so both must refetch or the row lingers.
      void trpcUtilsQueue.productionOrders.listTrackerOrders.invalidate();
      void trpcUtilsQueue.productionOrders.getTrackerCounts.invalidate();
    },
  });
  // Accepts either row shape — the Order Queue's QueuedOrder and the Orders tab's
  // TrackerOrder both carry the prod_orders.id as `id` (list_tracker_orders selects
  // o.id FROM prod_orders), which is what the mutation keys on.
  const handlePutOnHold = useCallback(
    (order: { id: string }) => {
      holdOrderMutation.mutate(
        { prod_order_id: order.id, hold_reason: 'manual' },
        {
          onSuccess: () => toast.success(t('orders.actions.putOnHoldSuccess')),
          onError: (error) => toast.error(error.message || t('orders.actions.putOnHoldFailed')),
        }
      );
    },
    [holdOrderMutation, t]
  );
  const skipHoldMutation = trpc.productionOrders.issues.skipOrderHold.useMutation({
    onSuccess: () => {
      void trpcUtilsQueue.productionOrders.getOrderQueue.invalidate();
      void trpcUtilsQueue.productionOrders.getQueueCounts.invalidate();
    },
  });
  const handleSkipHold = useCallback(
    (order: QueuedOrder) => {
      skipHoldMutation.mutate(
        { prod_order_id: order.id },
        {
          onSuccess: () => toast.success(t('orders.actions.skipHoldSuccess')),
          onError: (error) => toast.error(error.message || t('orders.actions.skipHoldFailed')),
        }
      );
    },
    [skipHoldMutation, t]
  );

  // Order Queue Move / Split: an order with no issue and no hold can be moved
  // or split straight from the queue. Entry points only. Move opens the same
  // order detail sheet the On hold tab opens (keyed by prod order id, no issue
  // needed); Split opens the existing resolve board. The queue row's `id` IS
  // the prod_orders id (see handlePutOnHold above).
  const callerOrgId = organization?.id;
  const canRelocateQueueRow = useCallback(
    (order: QueuedOrder) => canRelocateQueueOrder({ canResolveLocations, callerOrgId, order }),
    [canResolveLocations, callerOrgId]
  );
  const [moveProdOrderId, setMoveProdOrderId] = useState<string | null>(null);
  const handleQueueMove = useCallback((order: QueuedOrder) => setMoveProdOrderId(order.id), []);
  const handleQueueMoveClose = useCallback(() => {
    setMoveProdOrderId(null);
    // A moved order leaves this location's queue; refresh so the row goes.
    void trpcUtilsQueue.productionOrders.getOrderQueue.invalidate();
    void trpcUtilsQueue.productionOrders.getQueueCounts.invalidate();
  }, [trpcUtilsQueue]);
  const handleQueueSplit = useCallback(
    (order: QueuedOrder) => navigate(`/production/issues/resolve/${order.id}`),
    [navigate]
  );

  // Invoice printing state (BLU-328)
  // Flow: click menu → loading toast → data arrives → "ready" toast with Print Now button → user clicks → print dialog
  const invoiceEnabled = useLocationInvoiceEnabled(locationId);
  const [pendingInvoiceBatchId, setPendingInvoiceBatchId] = useState<string | undefined>();
  const [pendingInvoiceOrderId, setPendingInvoiceOrderId] = useState<string | undefined>();
  const [invoiceReady, setInvoiceReady] = useState(false);
  const { data: batchInvoiceData, isFetching: isFetchingBatchInvoice } = useInvoiceBatchData(pendingInvoiceBatchId, !!pendingInvoiceBatchId);
  const { data: orderInvoiceData, isFetching: isFetchingOrderInvoice } = useInvoiceOrderData(pendingInvoiceOrderId, !!pendingInvoiceOrderId);
  const logPrintEvent = trpc.productionOrders.logPrintEvent.useMutation();
  const trpcUtils = trpc.useUtils();
  const {
    printRef: invoicePrintRef,
    printInvoices,
    isPrinting: isPrintingInvoices,
  } = usePrintInvoices({
    onPrintComplete: () => {
      if (pendingInvoiceBatchId) {
        logPrintEvent.mutate({ batchId: pendingInvoiceBatchId, printType: 'INVOICE_BATCH' });
        trpcUtils.productionOrders.getInvoiceBatchData.invalidate({ batchId: pendingInvoiceBatchId });
      } else if (pendingInvoiceOrderId && orderInvoiceData?.batchId) {
        logPrintEvent.mutate({ batchId: orderInvoiceData.batchId, printType: 'INVOICE_ORDER' });
      }
      setPendingInvoiceBatchId(undefined);
      setPendingInvoiceOrderId(undefined);
      setInvoiceReady(false);
    },
  });

  // Store print function in ref so the toast action callback can access it
  const triggerPrintRef = useRef<(() => void) | null>(null);

  // When batch invoice data arrives, mark as ready and show "ready" toast
  useEffect(() => {
    if (!batchInvoiceData || !pendingInvoiceBatchId || isPrintingInvoices || invoiceReady) return;
    setInvoiceReady(true);
    const docTitle = `Invoices-Batch-${batchInvoiceData.batchNumber}`;
    const orderCount = batchInvoiceData.orders.length;

    // Store the print trigger so the toast button can call it
    triggerPrintRef.current = () => {
      toast.dismiss('invoice-ready');
      // Small delay so hidden container renders with the data
      setTimeout(() => printInvoices(docTitle), 150);
    };

    toast.success(
      t('invoice.readyBatch', { count: orderCount }),
      {
        id: 'invoice-ready',
        description: t('invoice.readyDesc'),
        duration: Infinity,
        action: {
          label: t('invoice.printNow'),
          onClick: () => triggerPrintRef.current?.(),
        },
        cancel: {
          label: t('invoice.dismiss'),
          onClick: () => {
            setPendingInvoiceBatchId(undefined);
            setInvoiceReady(false);
          },
        },
      }
    );
  }, [batchInvoiceData, pendingInvoiceBatchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When single order invoice data arrives, mark as ready and show "ready" toast
  useEffect(() => {
    if (!orderInvoiceData || !pendingInvoiceOrderId || isPrintingInvoices || invoiceReady) return;
    setInvoiceReady(true);
    const docTitle = `Invoice-${orderInvoiceData.orderName}`;

    triggerPrintRef.current = () => {
      toast.dismiss('invoice-ready');
      setTimeout(() => printInvoices(docTitle), 150);
    };

    toast.success(
      t('invoice.readyOrder', { orderName: orderInvoiceData.orderName }),
      {
        id: 'invoice-ready',
        description: t('invoice.readyDesc'),
        duration: Infinity,
        action: {
          label: t('invoice.printNow'),
          onClick: () => triggerPrintRef.current?.(),
        },
        cancel: {
          label: t('invoice.dismiss'),
          onClick: () => {
            setPendingInvoiceOrderId(undefined);
            setInvoiceReady(false);
          },
        },
      }
    );
  }, [orderInvoiceData, pendingInvoiceOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isInvoiceLoading = isFetchingBatchInvoice || isFetchingOrderInvoice || isPrintingInvoices;

  const handlePrintBatchInvoices = useCallback((batchId: string) => {
    if (isInvoiceLoading) return;
    setPendingInvoiceBatchId(batchId);
    setPendingInvoiceOrderId(undefined);
    setInvoiceReady(false);
    toast.loading(t('invoice.generating'), {
      id: 'invoice-ready',
      description: t('invoice.generatingDesc'),
    });
  }, [isInvoiceLoading]);

  const handlePrintOrderInvoice = useCallback((orderId: string) => {
    if (isInvoiceLoading) return;
    setPendingInvoiceOrderId(orderId);
    setPendingInvoiceBatchId(undefined);
    setInvoiceReady(false);
    toast.loading(t('invoice.generatingOne'), {
      id: 'invoice-ready',
      description: t('invoice.generatingOneDesc'),
    });
  }, [isInvoiceLoading]);

  // Reset to page 0 when search, sort, or bin filter changes
  useEffect(() => { setCurrentPage(0); }, [debouncedSearch, sortOrder, inBinOnly, setCurrentPage]);

  const isArchivedTab = view === 'batches' && batchTab === 'archived';
  // BLU-811. Declared here because it gates a query below AND every branch of
  // the result dispatch further down.
  const isCommittedTab = view === 'orders' && orderTab === 'committed';

  const {
    data: counts,
    isLoading: countsLoading,
    refetch: refetchCounts,
    isFetching: countsFetching,
  } = useTrackerCounts(locationId);

  // BLU-367: cross-org client filter. ONE selection for the whole page; it
  // survives switching between Orders / Queue / Batches.
  //
  // Declared ABOVE the data hooks because the selection is now a QUERY INPUT,
  // not a post-fetch array filter. The first cut filtered the loaded page in
  // the browser, which meant a client whose orders sat beyond page 1 read as
  // "no orders" — on a real floor the list runs to 150+ pages, so the filter
  // could not find what it was filtering for. The predicate now goes to the
  // server, so it spans every page and the pagination total is the filtered
  // total.
  //
  // `rows: []` is deliberate: the option list comes from the connected-client
  // roster (`orgIdToName`), not from whatever happens to be on screen. Deriving
  // options from rows would be circular now that the rows are already filtered.
  const clientFilter = useClientOrgFilter<{ organization_id?: string | null }>({
    rows: [],
    ownOrgId: organization?.id,
    ownOrgName: organization?.name,
    locationId,
    // Rows are empty by design (see above), so the "selected org is absent"
    // auto-clear would fire on every render. The location-change reset below
    // is the deliberate replacement for it.
    resetWhenAbsent: false,
  });
  const clientOrgFilter = clientFilter.clientOrgFilter;
  const setClientOrgFilter = clientFilter.setClientOrgFilter;
  const isClientFiltered = clientOrgFilter !== ALL_CLIENTS;
  /** Sent to the server. Undefined = all clients. */
  const clientOrgFilterParam = isClientFiltered ? clientOrgFilter : undefined;

  // Batch data (only fetched when view is 'batches' and not on archived tab)
  const batchResult = useTrackerBatches({
    locationId: view === 'batches' && !isArchivedTab ? locationId : undefined,
    tab: batchTab,
    sortOrder,
    page: currentPage,
    search: debouncedSearch,
  });

  // Order data (only fetched on the two batch-derived order tabs — the
  // Committed tab is a different population with its own RPC, and leaving this
  // enabled would poll `list_tracker_orders` for a table nobody is looking at.)
  const orderResult = useTrackerOrders({
    locationId,
    tab: orderTab,
    sortOrder,
    page: currentPage,
    search: debouncedSearch,
    enabled: view === 'orders' && orderTab !== 'committed',
    inBinOnly,
    clientOrganizationId: clientOrgFilterParam,
  });

  // Committed stock by order (BLU-811) — only on its own tab. The status filter is a
  // server-side query input, so the list/count/pager all reflect the filtered set.
  const committedResult = useCommittedOrders({
    locationId,
    sortOrder,
    page: currentPage,
    search: debouncedSearch,
    enabled: isCommittedTab,
    clientOrganizationId: clientOrgFilterParam,
    productionStatus,
  });

  // BASE (status-UNFILTERED) summary. Feeds the tab BADGE and — while no status filter
  // is active — the summary line. Runs for the whole Orders view so the badge exists
  // before you reach the tab. Crucially it carries NO productionStatus, so the badge
  // stays the FLOOR total regardless of the filter (a badge that shrinks when you pick
  // a sub-status reads as wrong). `includeBreakdown` is set only on the Committed tab,
  // so the extra prod_orders scan for `statusBreakdown` never touches the Active /
  // Shipped polls. Its `orderCount` also stays client-filtered, as before.
  const committedSummary = useCommittedSummary({
    locationId,
    enabled: view === 'orders',
    clientOrganizationId: clientOrgFilterParam,
    includeBreakdown: isCommittedTab,
  });

  // STATUS-FILTERED summary. Only its unit / order / distinct-SKU counts, and only
  // while a status filter is active on the Committed tab. A SEPARATE call (not the base
  // one) so the badge above can stay the unfiltered floor total; no breakdown here —
  // the base call owns the facet.
  const committedFilteredSummary = useCommittedSummary({
    locationId,
    enabled: isCommittedTab && isStatusFiltered,
    clientOrganizationId: clientOrgFilterParam,
    productionStatus,
  });

  // The summary line reads the filtered call under a status filter, the base call
  // otherwise. The badge and the breakdown facet always read the base call.
  const summaryForLine = isStatusFiltered ? committedFilteredSummary : committedSummary;
  const committedStatusBreakdown = committedSummary.data?.statusBreakdown;
  // The header hover-totals are only vouchable when the summary call that feeds them
  // actually returned. The paginated list RPC can succeed while this breakdown summary
  // times out on a big floor, and React Query keeps the last good `data` on an errored
  // refetch — so a bare `data?.x` could hand the header a STALE number under an error.
  // Gate on both: when not ready the totals go to the table as undefined and it
  // suppresses the tooltips (same as under a text search) rather than announcing 0/0/0.
  const summaryReady = !!summaryForLine.data && !summaryForLine.error;

  // Archived batches data (only fetched on archived tab)
  const archivedResult = useArchivedBatches({
    locationId,
    sortOrder,
    page: currentPage,
    search: debouncedSearch,
    enabled: isArchivedTab,
  });

  // Order Queue data (only fetched when view is 'queue')
  const queueResult = useTrackerQueueOrders({
    locationId,
    tab: queueTab,
    sortOrder,
    page: currentPage,
    search: debouncedSearch,
    enabled: view === 'queue',
    clientOrganizationId: clientOrgFilterParam,
  });
  const { data: queueCounts, isLoading: queueCountsLoading } = useTrackerQueueCounts(
    view === 'queue' ? locationId : undefined
  );

  // Options come from the connected-client roster at this location, so every
  // client is selectable regardless of which page you happen to be on.
  const clientFilterOptions = useMemo(
    () => Array.from(clientFilter.orgIdToName.keys()),
    [clientFilter.orgIdToName]
  );

  // A LOCATION change must clear the selection: the chosen client may not be
  // connected at the new floor, which would otherwise leave a filter applied
  // against a roster that no longer contains it.
  useEffect(() => {
    setClientOrgFilter(ALL_CLIENTS);
  }, [locationId, setClientOrgFilter]);

  // Changing the client OR status filter changes the size of the result set, so an
  // offset from the previous selection is meaningless — page 9 of a now-2-page list
  // would render empty. Reset to the first page whenever either selection changes.
  useEffect(() => {
    setCurrentPage(0);
  }, [clientOrgFilter, productionStatus, setCurrentPage]);

  // BLU-811: the status breakdown is a per-floor, per-client facet, so a location or
  // client change can make the selected status disappear; an order-tab / view change
  // leaves the Committed tab entirely. Reset to "all statuses" on any of them rather
  // than carrying a stale — possibly now-empty — status filter into a different set.
  useEffect(() => {
    setProductionStatus(undefined);
  }, [clientOrgFilter, locationId, orderTab, view]);

  // BLU-811/815: if the SELECTED status fully drains from the committed set mid-session
  // (the 5-min refetch can empty a bucket), the base breakdown loses that key — the
  // Select value then matches no item, so the trigger renders BLANK while the filtered
  // list returns zero rows. Reset to "all statuses" once the base summary has reloaded
  // successfully with the key gone. Guarded to the loaded-and-non-empty window so the
  // brief load gap (breakdown momentarily undefined) or a transient summary error never
  // clears a still-valid selection.
  useEffect(() => {
    if (productionStatus === undefined) return;
    if (committedSummary.isLoading || committedSummary.error) return;
    if (!committedStatusBreakdown || Object.keys(committedStatusBreakdown).length === 0) return;
    if (!(productionStatus in committedStatusBreakdown)) {
      setProductionStatus(undefined);
    }
  }, [productionStatus, committedStatusBreakdown, committedSummary.isLoading, committedSummary.error]);

  // The selection persists across views but only narrows the order views (all
  // three Orders tabs plus the Queue) — the Batches list is owner-org data with
  // no client dimension. Without this guard an empty batch list would claim
  // "no orders for <client>".
  const clientFilterAffectsView = isClientFiltered && view !== 'batches';

  // The committed RPC reads `total_count` off the FIRST returned row, so a page
  // past the end reports zero rows AND zero total — which also removes the pager
  // that would get the operator back. Clamp to page 1 instead of stranding them
  // on an empty page with no way off it.
  useEffect(() => {
    if (
      isCommittedTab &&
      !committedResult.isFetching &&
      committedResult.orders.length === 0 &&
      currentPage > 0
    ) {
      setCurrentPage(0);
    }
  }, [
    isCommittedTab,
    committedResult.isFetching,
    committedResult.orders.length,
    currentPage,
    setCurrentPage,
  ]);

  // ONE dispatch for the active view/tab, not seven parallel ternary ladders.
  // The old shape repeated the same five-branch decision for isLoading,
  // isFetching, error, refetch, totalPages, pagination and itemCount — adding a
  // tab meant remembering all seven, and forgetting one gave the new tab
  // another tab's spinner, error banner or pager. `itemCount` counts the
  // CLIENT-FILTERED rows (it drives both the empty state and
  // PaginationControls' `orderCount`, so the unfiltered array would render
  // empty table chrome beside a non-zero count).
  const activeResult = isArchivedTab
    ? { ...archivedResult, itemCount: archivedResult.batches.length }
    : view === 'batches'
      ? { ...batchResult, itemCount: batchResult.batches.length }
      : view === 'queue'
        ? { ...queueResult, itemCount: queueResult.orders.length }
        : isCommittedTab
          ? { ...committedResult, itemCount: committedResult.orders.length }
          : { ...orderResult, itemCount: orderResult.orders.length };

  const { isLoading, isFetching, error, totalPages, pagination, itemCount } = activeResult;
  const refetchData = activeResult.refetch;

  // The summary is part of the Committed tab's content, so its in-flight state
  // belongs in the same spinner as the table's — but ONLY there. The query runs
  // for the whole Orders view (it feeds the tab badge), so folding its
  // isFetching in unconditionally would spin the refresh control on the
  // Active/Shipped tabs where the summary is not even shown.
  const isRefreshing =
    isFetching ||
    countsFetching ||
    (isCommittedTab && (committedSummary.isFetching || committedFilteredSummary.isFetching));

  const refetchCommittedSummary = committedSummary.refetch;
  const refetchCommittedFilteredSummary = committedFilteredSummary.refetch;
  const handleRefresh = useCallback(() => {
    refetchCounts();
    refetchData();
    // Refetching a disabled query would fire an RPC for a view that is not on
    // screen, so each of these follows the same gate its own query uses.
    if (view === 'orders') refetchCommittedSummary();
    if (isCommittedTab && isStatusFiltered) refetchCommittedFilteredSummary();
  }, [
    refetchCounts,
    refetchData,
    refetchCommittedSummary,
    refetchCommittedFilteredSummary,
    view,
    isCommittedTab,
    isStatusFiltered,
  ]);

  // ==========================================================================
  // COMMITTED CSV EXPORT (BLU-811)
  // ==========================================================================

  const [isExportingCommitted, setIsExportingCommitted] = useState(false);
  // Search-aware total that drives the export gate AND the button's disabled
  // state. Reads the SAME total the pager uses (`listCommittedOrders`, which
  // honours search + client filter server-side) rather than the search-blind
  // summary count — otherwise a searched-down list is wrongly blocked as "too
  // many" and a zero-match search still offers an export that yields a
  // header-only CSV.
  const committedListTotal = committedResult.pagination?.total ?? 0;

  const handleExportCommitted = useCallback(async () => {
    if (!locationId || isExportingCommitted) return;

    // Checked BEFORE the fetch, off the search-aware total we already have: a
    // blocked export should cost nothing and should name the controls that
    // unblock it.
    if (committedListTotal > COMMITTED_EXPORT_ORDER_WARN_LIMIT) {
      toast.error(t('tracker.committed.export.tooManyTitle', { count: committedListTotal }), {
        description: t('tracker.committed.export.tooManyDesc'),
      });
      return;
    }

    setIsExportingCommitted(true);
    try {
      // Mirrors the on-screen filters exactly — including search and the client
      // filter, applied server-side, never post-hoc on a page of rows. The
      // server pages through PostgREST's 1000-row cap itself and flags
      // `truncated` if it stopped at its hard order cap.
      const result = await trpcUtils.productionOrders.exportCommittedOrders.fetch({
        locationId,
        clientOrganizationId: clientOrgFilterParam,
        search: debouncedSearch || undefined,
        sortOrder,
        // Mirror the on-screen status filter too, so the CSV is the same set the
        // operator is looking at.
        productionStatus,
      });

      const toCsvDate = (value: string | null | undefined) =>
        value ? format(new Date(value), CSV_DATE_TIME_FORMAT) : '';

      const csv = toCsvContent([
        [
          t('tracker.committed.export.col.order'),
          t('tracker.committed.export.col.orderDate'),
          t('tracker.committed.export.col.sku'),
          t('tracker.committed.export.col.units'),
          t('tracker.committed.export.col.shopifyStatus'),
          t('tracker.committed.export.col.orderStatus'),
          t('tracker.committed.export.col.productionStatus'),
          t('tracker.committed.export.col.shippedAt'),
          t('tracker.committed.export.col.client'),
        ],
        ...result.rows.map((row) => {
          // Hoisted: TS only narrows an indexed access when the index is a
          // const identifier, so the lookups cannot be inlined under
          // noUncheckedIndexedAccess.
          const shopifyStatus = row.shopify_status;
          const shopifyStatusKey = shopifyStatus
            ? FULFILLMENT_STATUS_LABEL_KEYS[shopifyStatus]
            : undefined;
          const productionStatus = row.production_status;
          const productionStatusKey = productionStatus
            ? STATUS_LABEL_KEYS[productionStatus]
            : undefined;

          return [
            row.order,
            toCsvDate(row.order_date),
            row.sku,
            row.committed_units,
            // Same vocabulary as the table: a file that says UNFULFILLED where
            // the screen said "Unfulfilled" reads as another system's export.
            shopifyStatus
              ? shopifyStatusKey
                ? t(shopifyStatusKey)
                : shopifyStatus.replace(/_/g, ' ')
              : '',
            row.order_status ?? '',
            productionStatus
              ? productionStatusKey
                ? t(productionStatusKey)
                : productionStatus.replace(/_/g, ' ')
              : t('tracker.committed.notInProduction'),
            toCsvDate(row.shipped_at),
            row.client,
          ];
        }),
      ]);

      downloadCsvFile(csv, `committed-orders-${format(new Date(), FILE_DATE_FORMAT)}.csv`);

      // The server tells us whether it stopped short of the full set. Announced
      // AFTER the download so the operator still gets the partial file they
      // asked for.
      if (result.truncated) {
        toast.warning(t('tracker.committed.export.truncatedTitle'), {
          description: t('tracker.committed.export.truncatedDesc'),
        });
      }
    } catch (err) {
      toast.error(t('tracker.committed.export.failedTitle'), {
        description: err instanceof Error ? err.message : t('tracker.committed.export.failedDesc'),
      });
    } finally {
      setIsExportingCommitted(false);
    }
  }, [
    locationId,
    isExportingCommitted,
    committedListTotal,
    trpcUtils,
    clientOrgFilterParam,
    debouncedSearch,
    sortOrder,
    productionStatus,
    t,
  ]);

  const searchPlaceholder = isArchivedTab
    ? t('tracker.searchArchived')
    : view === 'batches'
      ? t('tracker.searchBatches')
      : view === 'queue'
        ? t('tracker.searchQueue')
        : isCommittedTab
          ? t('tracker.searchCommitted')
          : t('tracker.searchOrders');

  // Empty state i18n keys
  const emptyStateKeys = view === 'orders'
    ? EMPTY_ORDER_KEYS[orderTab]
    : view === 'queue'
      ? EMPTY_QUEUE_KEYS[queueTab]
      : isArchivedTab
        ? EMPTY_BATCH_ARCHIVED_KEYS
        : EMPTY_BATCH_KEYS[batchTab as Exclude<BatchTab, 'archived'>] ?? EMPTY_BATCH_KEYS['in-progress'];

  // Tab label for pagination aria
  const activeTabLabel = view === 'orders' ? orderTab : view === 'queue' ? queueTab : batchTab;

  // Extracted so it renders on the EMPTY branch too. The client filter narrows
  // only the loaded page, so filtering to zero is a normal outcome with more
  // pages still behind it — without a pager there, the empty state tells the
  // operator to "try another page" while removing the only control that could.
  const paginationControls = totalPages > 1 ? (
    <PaginationControls
      paginationMode="paginated"
      currentPage={currentPage}
      totalPages={totalPages}
      hasMore={currentPage < totalPages - 1}
      isFetching={isFetching}
      orderCount={itemCount}
      currentOffset={pagination?.offset ?? 0}
      ariaLabel={`${activeTabLabel} ${view} pagination, page ${currentPage + 1} of ${totalPages}`}
      onPageChange={setCurrentPage}
      onLoadMore={() => {}}
    />
  ) : null;

  // No location selected
  if (!locationId) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="mt-4 text-lg font-medium">{t('tracker.noLocation')}</p>
            <p className="text-sm text-muted-foreground">
              {t('tracker.noLocationDesc')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6 animate-enter">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="page-title">{t('tracker.title')}</h1>
            <p className="page-description">
              {t('tracker.monitorBatches', { location: selectedLocation?.name })}
            </p>
          </div>
        </div>
      </div>

      {/* Top bar: View toggle + Tabs */}
      <div className="flex items-center gap-4">
        <ViewToggle view={view} onChange={setView} hideBatches={isCX()} />

        {/* View-specific tabs */}
        {view === 'orders' ? (
          <Tabs value={orderTab} onValueChange={(v) => setOrderTab(v as OrderTab)}>
            <TabsList className="h-10">
              <TabsTrigger value="active" className="gap-2 px-4">
                <Timer className="h-4 w-4" />
                {t('tracker.tabs.inProgress')}
                <TabBadge count={counts?.orders.active} isLoading={countsLoading} />
              </TabsTrigger>
              <TabsTrigger value="shipped" className="gap-2 px-4">
                <Package className="h-4 w-4" />
                {t('tracker.tabs.shipped')}
                <TabBadge count={counts?.orders.shipped} isLoading={countsLoading} />
              </TabsTrigger>
              {/* BLU-811. Badge comes from the committed summary, not
                  getTrackerCounts — one source for the badge and the line under
                  the table, which also means this badge (unlike its two
                  siblings) follows the client filter. */}
              <TabsTrigger value="committed" className="gap-2 px-4">
                <Boxes className="h-4 w-4" />
                {t('tracker.tabs.committed')}
                <TabBadge
                  count={committedSummary.data?.orderCount}
                  isLoading={committedSummary.isLoading}
                />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : view === 'queue' ? (
          <Tabs value={queueTab} onValueChange={(v) => setQueueTab(v as QueueViewTab)}>
            <TabsList className="h-10">
              <TabsTrigger value="ready" className="gap-2 px-4">
                <ListChecks className="h-4 w-4" />
                {t('tracker.tabs.ready')}
                <TabBadge count={queueCounts?.ready} isLoading={queueCountsLoading} />
              </TabsTrigger>
              <TabsTrigger value="all" className="gap-2 px-4">
                <ClipboardList className="h-4 w-4" />
                {t('tracker.tabs.all')}
                <TabBadge count={queueCounts?.all} isLoading={queueCountsLoading} />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <Tabs value={batchTab} onValueChange={(v) => setBatchTab(v as BatchTab)}>
            <TabsList className="h-10">
              <TabsTrigger value="in-progress" className="gap-2 px-4">
                <Timer className="h-4 w-4" />
                {t('tracker.tabs.inProgress')}
                <TabBadge count={counts?.batches.inProgress} isLoading={countsLoading} />
              </TabsTrigger>
              <TabsTrigger value="complete" className="gap-2 px-4">
                <CheckCircle2 className="h-4 w-4" />
                {t('tracker.tabs.complete')}
                <TabBadge count={counts?.batches.complete} isLoading={countsLoading} />
              </TabsTrigger>
              <TabsTrigger value="archived" className="gap-2 px-4">
                <Archive className="h-4 w-4" />
                {t('tracker.tabs.archived')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Search + Sort + Refresh bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'newest' | 'oldest')}>
          <SelectTrigger className="w-[160px]">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{t('tracker.sort.newest')}</SelectItem>
            <SelectItem value="oldest">{t('tracker.sort.oldest')}</SelectItem>
          </SelectContent>
        </Select>
        {view === 'orders' && orderTab === 'active' && (
          <Toggle
            pressed={inBinOnly}
            onPressedChange={setInBinOnly}
            size="sm"
            variant="outline"
            className="gap-1.5 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
          >
            <Inbox className="h-4 w-4" />
            {t('tracker.filter.inBin')}
          </Toggle>
        )}
        {/* BLU-367: only renders at a shared location with connected clients —
            single-tenant operators see no chrome change. Not shown on the
            Batches view: a batch belongs to the owner org, so there is nothing
            to filter (the client lives on the order sub-rows inside it). */}
        {clientFilter.showClientFilter && view !== 'batches' && (
          <ClientFilterDropdown
            value={clientOrgFilter}
            onChange={clientFilter.setClientOrgFilter}
            ownOrgId={organization?.id}
            orgIdToName={clientFilter.orgIdToName}
            distinctOrgsInData={clientFilterOptions}
          />
        )}
        {/* BLU-811 production-status filter — Committed tab only, immediately after the
            client filter. Options + counts come from the status breakdown facet
            (client-scoped, status-filter-independent), so the option list stays
            complete whichever status is selected. Rendered only once the facet has
            keys — nothing committed means nothing to filter. */}
        {isCommittedTab &&
          committedStatusBreakdown &&
          Object.keys(committedStatusBreakdown).length > 0 && (
            <StatusFilterDropdown
              value={productionStatus}
              onChange={setProductionStatus}
              statusBreakdown={committedStatusBreakdown}
            />
          )}
        {/* Auxiliary action before the refresh control, per DESIGN.md §3.3's
            left-to-right cluster order. Disabled at zero rows: an export of
            nothing is a file the operator has to open to discover is empty. */}
        {isCommittedTab && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={isExportingCommitted || committedListTotal === 0}
            onClick={handleExportCommitted}
          >
            {isExportingCommitted ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4" aria-hidden="true" />
            )}
            {isExportingCommitted
              ? t('tracker.committed.export.busy')
              : t('tracker.committed.export.button')}
          </Button>
        )}
        <RefreshButton isFetching={isRefreshing && !isLoading} onRefresh={handleRefresh} />
      </div>

      {/* Committed summary — deliberately ABOVE the loading/empty/error branches
          so the figure survives a refetch and is still there on an empty table
          (a "0 orders, 0 units" reading is the answer to "where did my
          committed stock go", not a state to hide). On a summary ERROR the line
          is removed entirely: zeros nobody can vouch for are worse than
          silence, because they look like a real reconciliation result.

          Hidden while a SEARCH is active: the summary RPC is search-blind
          (client-filtered only), so its totals would contradict the filtered
          rows below it — a searched-down list of 2 orders under a line reading
          "51 units across 25 orders". The client filter IS reflected in the
          summary, so only a text search forces the hide. */}
      {isCommittedTab && !debouncedSearch && (
        summaryForLine.isLoading ? (
          <Skeleton className="h-4 w-80" />
        ) : summaryForLine.data && !summaryForLine.error ? (
          <div className="space-y-0.5">
            {/* Self-contained clauses, each carrying its own leading separator (en
                space, zh comma), so each language owns its punctuation and each count
                keeps an independent plural — a literal-space join produced
                ungrammatical Chinese, and i18next nesting hid the fragment keys from
                the parity scanner. Under a status filter the counts are the FILTERED
                set; the inventory clause is dropped then, because inventory_committed
                has no production-status dimension, so pairing a status-filtered unit
                count with a whole-floor inventory number would read as false drift. */}
            <p role="status" className="text-sm text-muted-foreground tabular-nums">
              {t('tracker.committed.summary.line', {
                count: summaryForLine.data.unitCount,
                location: selectedLocation?.name ?? '',
              })}
              {t('tracker.committed.summary.ordersFragment', {
                count: summaryForLine.data.orderCount,
              })}
              {t('tracker.committed.summary.skusFragment', {
                count: summaryForLine.data.distinctSkus,
              })}
              {!isStatusFiltered &&
                t('tracker.committed.summary.inventoryFragment', {
                  count: summaryForLine.data.inventoryCommitted,
                })}
            </p>
            {/* Why rows vanish on their own. Without it the tab looks like it is
                losing orders. */}
            <p className="text-xs text-muted-foreground">
              {t('tracker.committed.summary.note')}
            </p>
          </div>
        ) : null
      )}

      {/* Error state */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{t('tracker.failedToLoad', { view: view === 'batches' ? t('tracker.viewBatches').toLowerCase() : view === 'queue' ? t('tracker.viewQueue').toLowerCase() : t('tracker.viewOrders').toLowerCase() })}</span>
            <Button variant="outline" size="sm" onClick={() => refetchData()}>
              {t('dashboard.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Content */}
      {isLoading ? (
        view === 'batches'
          ? <TrackerBatchTable batches={[]} isLoading />
          : view === 'queue'
            ? <TrackerQueueTable orders={[]} isLoading />
            : isCommittedTab
              ? <CommittedOrderTable orders={[]} isLoading />
              : <TrackerOrderTable orders={[]} isLoading />
      ) : itemCount === 0 && !isFetching ? (
        <>
        <div className="empty-state">
          <div className="empty-state-icon">
            {isArchivedTab
              ? <Archive className="h-6 w-6 text-muted-foreground" />
              : view === 'batches'
                ? <Layers className="h-6 w-6 text-muted-foreground" />
                : view === 'queue'
                  ? <Hourglass className="h-6 w-6 text-muted-foreground" />
                  : isCommittedTab
                    ? <Boxes className="h-6 w-6 text-muted-foreground" />
                    : <ClipboardList className="h-6 w-6 text-muted-foreground" />
            }
          </div>
          <p className="empty-state-title">
            {clientFilterAffectsView
              ? t('tracker.noClientMatch', { view: view === 'queue' ? t('tracker.viewQueue').toLowerCase() : t('tracker.viewOrders').toLowerCase(), client: clientFilter.selectedClientName ?? '' })
              : debouncedSearch
              ? t('tracker.noMatch', { view: view === 'batches' ? t('tracker.viewBatches').toLowerCase() : view === 'queue' ? t('tracker.viewQueue').toLowerCase() : t('tracker.viewOrders').toLowerCase(), search: debouncedSearch })
              : t(emptyStateKeys.titleKey)}
          </p>
          <p className="empty-state-description">
            {clientFilterAffectsView
              ? t('tracker.adjustClientFilter')
              : debouncedSearch
              ? t('tracker.adjustSearch')
              : t(emptyStateKeys.descKey)}
          </p>
        </div>
        {paginationControls}
        </>
      ) : (
        <>
          <div className={cn(isFetching && !isLoading && 'opacity-60 transition-opacity')}>
            {isArchivedTab ? (
              <TrackerBatchTable
                batches={archivedResult.batches}
                isArchived
                locationId={locationId}
                shopifyDomain={shopifyDomain}
                invoiceEnabled={invoiceEnabled}
                onPrintBatchInvoices={handlePrintBatchInvoices}
                onPrintOrderInvoice={handlePrintOrderInvoice}
              />
            ) : view === 'batches' ? (
              <TrackerBatchTable
                batches={batchResult.batches}
                locationId={locationId}
                shopifyDomain={shopifyDomain}
                invoiceEnabled={invoiceEnabled}
                onPrintBatchInvoices={handlePrintBatchInvoices}
                onPrintOrderInvoice={handlePrintOrderInvoice}
              />
            ) : view === 'queue' ? (
              <TrackerQueueTable
                orders={queueResult.orders}
                shopifyDomain={shopifyDomain}
                onPutOnHold={canHoldOrders ? handlePutOnHold : undefined}
                onSkipHold={canHoldOrders ? handleSkipHold : undefined}
                onMove={handleQueueMove}
                onSplit={handleQueueSplit}
                canRelocate={canRelocateQueueRow}
              />
            ) : isCommittedTab ? (
              <CommittedOrderTable
                orders={committedResult.orders}
                shopifyDomain={shopifyDomain}
                // Header totals track the CURRENTLY DISPLAYED (status-filtered) list;
                // the breakdown is the client-scoped facet (all statuses). Passed only
                // when the summary is READY — undefined while it loads or errors, so the
                // table suppresses the tooltips instead of announcing an unvouchable 0.
                // Tooltips also hide under a text search — the summary is search-blind.
                orderCount={summaryReady ? summaryForLine.data?.orderCount : undefined}
                unitCount={summaryReady ? summaryForLine.data?.unitCount : undefined}
                distinctSkus={summaryReady ? summaryForLine.data?.distinctSkus : undefined}
                statusBreakdown={committedStatusBreakdown}
                searchActive={!!debouncedSearch}
              />
            ) : (
              <TrackerOrderTable
                orders={orderResult.orders}
                shopifyDomain={shopifyDomain}
                locationId={locationId}
                invoiceEnabled={invoiceEnabled}
                onPrintOrderInvoice={handlePrintOrderInvoice}
                onPutOnHold={canHoldOrders ? handlePutOnHold : undefined}
                onClientQcRedoDone={handleClientQcRedoDone}
              />
            )}
          </div>
          {paginationControls}
        </>
      )}

      {/* Order Queue "Move to another location": the same order detail sheet the
          On hold tab opens, with the same props (no issue, keyed by prod order
          id). Mounted only for a user who can be offered the action. */}
      {canResolveLocations && (
        <OrderDetailModal
          open={moveProdOrderId !== null}
          onClose={handleQueueMoveClose}
          issueId={null}
          context="on_hold"
          prodOrderId={moveProdOrderId}
          canSendBack={false}
        />
      )}

      {/* Hidden invoice print container (BLU-328) — only render the active print target */}
      <div ref={invoicePrintRef} style={{ display: 'none' }} aria-hidden="true">
        {pendingInvoiceBatchId && batchInvoiceData?.orders.map((order) => (
          <OrderInvoice
            key={order.orderId}
            order={order}
            locationName={batchInvoiceData.locationName}
            batchNumber={batchInvoiceData.batchNumber}
          />
        ))}
        {pendingInvoiceOrderId && orderInvoiceData && (
          <OrderInvoice
            order={orderInvoiceData}
            locationName={orderInvoiceData.locationName}
            batchNumber={orderInvoiceData.batchNumber ?? 0}
          />
        )}
      </div>
    </div>
  );
}
